/* =====================================================================
   mapsLoader.js — loads the Google Maps JS API with the TENANT'S OWN key
   ---------------------------------------------------------------------
   Ported from the admin's MapsLoaderService
   (dashboard/src/app/shared/services/maps-loader.service.ts) with ONE
   deliberate difference: there is NO fallback to the bundled AppDate build
   key. The admin is behind a login; this bundle is a PUBLIC page, so a
   fallback here would spend AppDate's Google quota for every tenant that
   never registered a key. No key => resolve(null) => the caller renders
   today's plain inputs (the same "hide the affordance entirely" degradation
   the customer RN app chose).

   Why not src/hooks/useScript.js: it REMOVES the <script> on unmount, and
   the Maps API refuses to load twice — a remount would permanently break
   the page. The promise below is cached at module scope so the script is
   installed exactly once per session.
   ===================================================================== */

// Libraries the app actually uses. 'places' powers address autocomplete;
// 'geocoding' is needed because a Geocoder can be constructed before any map
// exists. Requested up front so no caller has to know about loading order.
const DEFAULT_LIBRARIES = ['maps', 'marker', 'geocoding', 'places'];

let loadPromise = null;
let loadedWithKey = null;

const bootstrap = (key, libraries) =>
  // Google's official inline bootstrap, verbatim in behaviour: it defines
  // google.maps.importLibrary and appends the real script on first use.
  new Promise((resolve, reject) => {
    const g = window.google || (window.google = {});
    const maps = g.maps || (g.maps = {});
    if (maps.importLibrary) { resolve(); return; }
    const requested = new Set();
    let scriptPromise = null;
    const startLoad = () => scriptPromise || (scriptPromise = new Promise((res, rej) => {
      const script = document.createElement('script');
      const params = new URLSearchParams();
      params.set('key', key);
      params.set('v', 'weekly');
      params.set('libraries', [...requested].join(','));
      params.set('callback', 'google.maps.__ib__');
      script.src = `https://maps.googleapis.com/maps/api/js?${params}`;
      maps.__ib__ = res;
      script.onerror = () => rej(new Error('The Google Maps JavaScript API could not load.'));
      script.nonce = document.querySelector('script[nonce]')?.nonce || '';
      document.head.append(script);
    }));
    maps.importLibrary = (lib, ...rest) =>
      requested.add(lib) && startLoad().then(() => maps.importLibrary(lib, ...rest));
    resolve();
  }).then(() => Promise.all(libraries.map(lib => window.google.maps.importLibrary(lib))));

/**
 * Load the Maps JS API for this tenant.
 * @param {string} key   feathersStore.tenantMapsKey() — the tenant's own key
 * @returns {Promise<object|null>} google.maps once ready, or null when the
 *          tenant has no key (caller must degrade to plain inputs).
 */
export const loadGoogleMaps = (key, libraries = DEFAULT_LIBRARIES) => {
  const tenantKey = `${key ?? ''}`.trim();

  // No tenant key => never touch Google at all. Not an error: it is the
  // normal state for every tenant that has not opted in and paid.
  if (!tenantKey) return Promise.resolve(null);

  if (loadPromise) {
    // The API cannot be reloaded with a different key. Settings arrive once
    // per session, so this only fires if a tenant edits the key live.
    if (loadedWithKey !== tenantKey) {
      console.warn('Google Maps already loaded with a different key; reload the page to switch keys.');
    }
    return loadPromise;
  }

  loadedWithKey = tenantKey;
  loadPromise = bootstrap(tenantKey, libraries)
    .then(() => window.google.maps)
    .catch(err => {
      // Allow a later attempt (e.g. the user reopens the address form after a
      // network blip). Note the Maps API itself may not fully recover in-session.
      loadPromise = null;
      loadedWithKey = null;
      throw err;
    });

  return loadPromise;
};

export const isMapsLoaded = () => Boolean(window.google?.maps?.importLibrary);

export default loadGoogleMaps;
